import { snapToPixel } from '../util/math.js';
import { fieldMetrics, screenXForYard } from '../field/metrics.js';
import { ballAnchorPointsForPlayer, clonePlayers, fieldGoalSnapBallPoint } from '../player/model.js';
import type { ArcadeReplayFrameSnapshot, ArcadeReplayPackage } from '../../arcade/contracts.js';

const ARCADE_FIELD_WIDTH = 53.33;
const KICK_SHELL_BLEND_FRAMES = 12;
const KICK_SNAP_BLEND_END_FRAME = 18;
const KICK_HOLD_BLEND_END_FRAME = 24;

function mapFieldXToWorldX(fieldX: number, direction: number): number {
  return screenXForYard(fieldX - 10, direction);
}

function mapFieldYToWorldY(fieldY: number, metrics: ReturnType<typeof fieldMetrics>): number {
  return snapToPixel(metrics.fieldTop + (fieldY / ARCADE_FIELD_WIDTH) * metrics.fieldHeight);
}

function applyReplayFrameToPlayers(players: any[], replayFrame: ArcadeReplayFrameSnapshot, direction: number, metrics: ReturnType<typeof fieldMetrics>): any[] {
  const mapped = clonePlayers(players);
  const byRole = new Map(
    replayFrame.players
      .filter((player) => player.rendererRole)
      .map((player) => [player.rendererRole, player]),
  );

  return mapped.map((player) => {
    const source = byRole.get(player.role);
    if (!source) {
      return { ...player, moving: false };
    }
    return {
      ...player,
      x: mapFieldXToWorldX(source.x, direction),
      y: mapFieldYToWorldY(source.y, metrics),
      moving: Math.hypot(source.vx, source.vy) > 0.03 && source.engagementState !== 'tackled',
      down: source.engagementState === 'tackled',
      tackled: source.engagementState === 'tackled',
      arcadeState: source.state,
      arcadeAssignment: source.assignmentKind,
    };
  });
}

function blendReplayPlayersFromShell(
  shellPlayers: any[],
  replayPlayers: any[],
  replayFrame: ArcadeReplayFrameSnapshot,
): any[] {
  const blendAmount = Math.max(0, Math.min(1, replayFrame.simFrame / KICK_SHELL_BLEND_FRAMES));
  if (blendAmount >= 1) {
    return replayPlayers;
  }

  return replayPlayers.map((player) => {
    const shell = shellPlayers.find((candidate) => candidate.role === player.role);
    if (!shell) {
      return player;
    }
    return {
      ...player,
      x: snapToPixel(shell.x + (player.x - shell.x) * blendAmount),
      y: snapToPixel(shell.y + (player.y - shell.y) * blendAmount),
      moving: blendAmount > 0.5 ? player.moving : false,
    };
  });
}

function ownedBallPointForReplayFrame(
  replayFrame: ArcadeReplayFrameSnapshot,
  players: any[],
  direction: number,
  metrics: ReturnType<typeof fieldMetrics>,
) {
  if (!replayFrame.ball.ownerRendererRole) {
    return null;
  }
  const owner = players.find((player) => player.role === replayFrame.ball.ownerRendererRole);
  const ownerSnapshot = replayFrame.players.find((player) => player.rendererRole === replayFrame.ball.ownerRendererRole);
  if (!owner) {
    return null;
  }
  const anchors = ballAnchorPointsForPlayer(owner, { direction }, ownerSnapshot?.side === 'defense' ? 'defense' : 'offense');
  const primary = anchors[anchors.length - 1] || anchors[0];
  return {
    ballX: primary?.x ?? mapFieldXToWorldX(replayFrame.ball.x, direction),
    ballY: primary?.y ?? snapToPixel(mapFieldYToWorldY(replayFrame.ball.y, metrics) + 8),
    arcadeState: replayFrame.ball.state,
    arcadeOwnerRole: replayFrame.ball.ownerRendererRole,
  };
}

function rawBallPointForReplayFrame(replayFrame: ArcadeReplayFrameSnapshot, direction: number, metrics: ReturnType<typeof fieldMetrics>) {
  const groundBallY = mapFieldYToWorldY(replayFrame.ball.y, metrics) + 8;
  const flightLift = replayFrame.ball.state === 'kick-flight'
    ? replayFrame.ball.z * 18
    : replayFrame.ball.state === 'kick-blocked'
      ? replayFrame.ball.z * 10
      : replayFrame.ball.z * 5;
  return {
    ballX: mapFieldXToWorldX(replayFrame.ball.x, direction),
    ballY: snapToPixel(groundBallY - flightLift),
    arcadeHeight: Math.max(0, replayFrame.ball.z),
    arcadeGroundY: snapToPixel(groundBallY + 4),
    arcadeState: replayFrame.ball.state,
    arcadeOwnerRole: replayFrame.ball.ownerRendererRole,
  };
}

function blendBallPoint(from: any, to: any, amount: number) {
  const clamped = Math.max(0, Math.min(1, amount));
  return {
    ballX: snapToPixel(from.ballX + (to.ballX - from.ballX) * clamped),
    ballY: snapToPixel(from.ballY + (to.ballY - from.ballY) * clamped),
    arcadeHeight: (from.arcadeHeight ?? 0)
      + ((to.arcadeHeight ?? 0) - (from.arcadeHeight ?? 0)) * clamped,
    arcadeGroundY: snapToPixel(
      (from.arcadeGroundY ?? from.ballY + 4)
        + ((to.arcadeGroundY ?? to.ballY + 4) - (from.arcadeGroundY ?? from.ballY + 4)) * clamped,
    ),
    arcadeState: to.arcadeState ?? from.arcadeState,
    arcadeOwnerRole: to.arcadeOwnerRole ?? from.arcadeOwnerRole,
  };
}

function ballPointForReplayFrame(
  replayFrame: ArcadeReplayFrameSnapshot,
  mappedOffense: any[],
  mappedDefense: any[],
  direction: number,
  metrics: ReturnType<typeof fieldMetrics>,
  losX: number,
) {
  const allPlayers = [...mappedOffense, ...mappedDefense];
  const rawBallPoint = rawBallPointForReplayFrame(replayFrame, direction, metrics);
  const owned = ownedBallPointForReplayFrame(replayFrame, allPlayers, direction, metrics);
  if (replayFrame.ball.state === 'hold' && owned && replayFrame.ball.ownerRendererRole === 'HOLDER' && replayFrame.simFrame <= KICK_HOLD_BLEND_END_FRAME) {
    const holdBlend = (replayFrame.simFrame - KICK_SNAP_BLEND_END_FRAME) / Math.max(1, KICK_HOLD_BLEND_END_FRAME - KICK_SNAP_BLEND_END_FRAME);
    return blendBallPoint(rawBallPoint, owned, holdBlend);
  }
  if (owned) {
    return owned;
  }

  const snapper = mappedOffense.find((player) => player.role === 'BLOCK4');
  if (replayFrame.ball.state === 'snap' && snapper) {
    return blendBallPoint(
      fieldGoalSnapBallPoint(snapper as any, direction, losX, snapper.y + 8),
      rawBallPoint,
      replayFrame.simFrame / Math.max(1, KICK_SNAP_BLEND_END_FRAME),
    );
  }

  return rawBallPoint;
}

export function buildArcadeFieldGoalLiveSequence(context: any): any[] {
  const replay = context.play?.arcadeReplay as ArcadeReplayPackage | undefined;
  if (!replay?.frames?.length) {
    return [];
  }

  const metrics = fieldMetrics();
  return replay.frames.map((replayFrame) => {
    const offensePlayers = blendReplayPlayersFromShell(
      context.formation.offense,
      applyReplayFrameToPlayers(context.formation.offense, replayFrame, context.direction, metrics),
      replayFrame,
    );
    const defensePlayers = blendReplayPlayersFromShell(
      context.formation.defense,
      applyReplayFrameToPlayers(context.formation.defense, replayFrame, context.direction, metrics),
      replayFrame,
    );
    return {
      offensePlayers,
      defensePlayers,
      ball: ballPointForReplayFrame(replayFrame, offensePlayers, defensePlayers, context.direction, metrics, context.losX),
    };
  });
}
